import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';

// Set on <html> only while a transition runs, so pages can scope their view-transition-name rules to it
// (see components/common/AuthPageLink/AuthPageLink.scss for why).
const VIEW_TRANSITION_ACTIVE_CLASS_NAME = 'view-transition-active';

let activeTransition = null;
// Set from startViewTransition() until its update callback has run: a second call in the same task would skip the
// first transition, but its callback still runs — i.e. navigate() twice and a duplicate history entry.
let isUpdatePending = false;

const isReducedMotionPreferred = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// The app's scroller is <body> (global.scss: html/body are position: fixed + overflow: auto), so window.scrollTo() is
// a no-op here. A full page reload used to start the next page at the top; an in-app navigate() keeps the offset —
// and the links sit at the bottom of their forms, so on mobile the next page would open scrolled past its header.
// (Next to the brand panel the auth pages scroll their form column instead — TRN-7538 — but that column is
// re-mounted with the new route, so it starts at the top anyway.)
const resetPageScroll = () => {
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
};

// TRN-7473 — navigate() wrapped in the browser's View Transitions API, so the DOM swap between two routes is
// animated by the `::view-transition-*` rules of the pages involved. react-router's own `viewTransition` option
// only works with a data router (createBrowserRouter), and this app uses <BrowserRouter> + <Routes> — hence the
// manual document.startViewTransition() here. flushSync makes the new route render synchronously inside the
// callback, so the browser snapshots the actual new page, not the old one. Falls back to a plain navigate() where
// the API is missing (older Firefox/Safari) or reduced motion is requested.
export const useViewTransitionNavigate = () => {
  const navigate = useNavigate();

  return useCallback(
    (to, options) => {
      if (!document.startViewTransition || isReducedMotionPreferred()) {
        resetPageScroll();
        navigate(to, options);
        return;
      }

      if (isUpdatePending) return;
      isUpdatePending = true;

      const root = document.documentElement;
      root.classList.add(VIEW_TRANSITION_ACTIVE_CLASS_NAME);

      const transition = document.startViewTransition(() => {
        isUpdatePending = false;
        // Before the new route renders, so the browser snapshots the new page from its top.
        resetPageScroll();
        flushSync(() => navigate(to, options));
      });
      activeTransition = transition;

      // A newer transition (e.g. a click right after this one has rendered) skips this one — leave the class to
      // that one to remove.
      const cleanUp = () => {
        if (activeTransition !== transition) return;

        activeTransition = null;
        isUpdatePending = false;
        root.classList.remove(VIEW_TRANSITION_ACTIVE_CLASS_NAME);
      };
      transition.finished.then(cleanUp, cleanUp);
    },
    [navigate]
  );
};
